<template>
	<AppDialog disableMaxWidth :dialogName="dialogName" width="750">
		<div class="pt-4 px-9" v-if="!showSendInviteDialog">
			<div>
				<div class="display-flex flex-row flex-align-center">
					<SIcon class="mr-2" name="successfull-connection" />
					<div class="header">Space created</div>
				</div>
				<div class="name pb-4">
					<div class="title">Invite Collaborators</div>
					<UserSelectCombobox :comboboxSelection.sync="comboboxSelection">
						<template #right-side-prepend>
							<ProjectRoleSelectorDisplay :showRemove="false" :role.sync="permissionSelection" />
						</template>
					</UserSelectCombobox>
				</div>
				<Row v-if="showEmailInvitationDescription" class="flex-align-center justify-between mb-2">
					<Row class="flex-align-center">
						<SIcon name="email-invitation-chip" class="mr-2" />
						<h5 class="email-invitation-text pt-1">{{ amountOfMembersText }}</h5>
					</Row>
					<RoleSelector :disabledRoles="disabledRoles" :value="inviteUserRole" :onChange="(role) => (inviteUserRole = role)" />
				</Row>
			</div>
			<div class="dialog-bottom text-align-center mt-4">
				<PrimaryButton @click="sendCollaborate">Done</PrimaryButton>
			</div>
		</div>
		<InvitationConfirmations
			v-else
			:canInviteUsers="canInviteUsers"
			:isProject="true"
			:sendingInviteAndShare="isSending"
			:emailsToInvite="emailsToInvite"
			:usersSelectedCount="usersSelectedCount"
			:nonAccountOwnerAllowShare="nonAccountOwnerAllowShare"
			@onBackClicked="showSendInviteDialog = false"
			@onShareClicked="inviteAndCollaborate"
		/>
	</AppDialog>
</template>

<script>
import isString from 'lodash/isString';
import { mapActions } from 'vuex';

import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import RoleSelector from '@/components/ReactComponentsDataProviders/RoleSelector.vue';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import InvitationConfirmations from '@/components/dialogs/ShareModuleDialog/InvitationConfirmations';
import UserSelectCombobox from '@/components/dialogs/ShareModuleDialog/UserSelectCombobox';
import ProjectRoleSelectorDisplay from '@/components/dialogs/projects/settings/tabs/DataRow/ProjectRoleSelectorDisplay.vue';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { getGroups } from '@/store/modules/accountGroups/actions';
import { collaborateNewProject } from '@/store/modules/projects/actions';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { AccountRoles } from '@/utils/account-service.const';
import CollaborateProjectsService from '@/utils/collaborate-project-service';
import { pluralize } from '@/utils/language-service';
import { PROJECT_ROLES } from '@/utils/projects.const';
export default {
	name: 'CollaborateNewProjectDialog',
	props: {
		dialogPromiseHandlers: { type: Object },
		projectId: { type: Number },
	},
	components: {
		ProjectRoleSelectorDisplay,
		AppDialog,
		UserSelectCombobox,
		InvitationConfirmations,
		RoleSelector,
	},
	computed: {
		...mapGettersShorthand({ hasAccountPermission }),
		disabledRoles() {
			return [AccountRoles.LiteEndUser, AccountRoles.PremiumEndUser, AccountRoles.StandardEndUser];
		},
		usersInvitedByMail() {
			return this.comboboxSelection.filter((selection) => typeof selection === 'string' || selection.suggestion);
		},
		showEmailInvitationDescription() {
			return this.usersInvitedByMail.length > 0;
		},
		amountOfMembersText() {
			return `${pluralize('member', this.usersInvitedByMail.length, true)} will be invited to Ply, choose their workspace role:`;
		},
		canCollaborate() {
			return this.hasAccountPermission(AccountPermissions.CollaborateProjects);
		},
		canInviteUsers() {
			return this.hasAccountPermission(AccountPermissions.InviteUsers);
		},
		canViewGroups() {
			return this.hasAccountPermission(AccountPermissions.ViewGroups);
		},
		emailsToInvite() {
			return this.comboboxSelection.filter((u) => isString(u));
		},
		isEmailInvitesExists() {
			return this.emailsToInvite.length > 0;
		},
		existingUsersOrGroupsSelected() {
			return this.comboboxSelection.filter((u) => !isString(u));
		},
		usersSelected() {
			return this.existingUsersOrGroupsSelected.filter((x) => !x.isGroup);
		},
		usersSelectedCount() {
			return this.usersSelected?.length || 0;
		},
		groupsSelected() {
			const usersToShareWith = this.existingUsersOrGroupsSelected;
			return usersToShareWith.filter((x) => x.isGroup).map((x) => x.id);
		},
		allUsersToCollaborateWith() {
			const emailsToInvite = this.canInviteUsers ? this.emailsToInvite.map((e) => ({ email: e })) : [];
			return [...this.usersSelected, ...emailsToInvite];
		},
		nonAccountOwnerAllowShare() {
			return this.isSending || this.usersSelectedCount === 0;
		},
		hasSelection() {
			return this.comboboxSelection.length > 0;
		},
	},
	methods: {
		...mapActions([collaborateNewProject.fullPath, getGroups.fullPath]),
		close() {
			this.dialogPromiseHandlers.resolve();
			AppDialogManagerService.closeCurrentDialog();
		},
		sendCollaborate() {
			if (this.isEmailInvitesExists) {
				this.showSendInviteDialog = true;
			} else {
				this.inviteAndCollaborate();
			}
		},
		async inviteAndCollaborate() {
			try {
				const { projectId, emailMessage, isSendMail, groupsSelected, allUsersToCollaborateWith, inviteUserRole } = this;
				this.isSending = true;

				if (
					await CollaborateProjectsService.inviteAndCollaborate({
						collaborateNewProjectAction: this[collaborateNewProject.fullPath],
						projectId,
						emailMessage,
						isSendMail,
						groupsSelected,
						inviteUserRole,
						allUsersToCollaborateWith,
						role: this.permissionSelection,
					})
				) {
					this.comboboxSelection = [];
					this.$toast.success('Successfully added collaborators');
				}
			} catch (err) {
				console.log(err);
				this.$toast.error('We had an issue while collaborating');
			} finally {
				this.isSending = false;
				this.close();
			}
		},
	},
	mounted() {
		if (this.canViewGroups) {
			this[getGroups.fullPath]();
		}
	},
	data: () => ({
		dialogName: 'CollaborateNewProjectDialog',
		placeholder: 'Add teammates, groups or emails',
		comboboxSelection: [],
		isSending: false,
		showSendInviteDialog: false,
		permissionSelection: PROJECT_ROLES.ADMIN,
		inviteUserRole: AccountRoles.LimitedCreator,
	}),
};
</script>

<style scoped lang="scss">
.header {
	font-size: 18px;
	font-weight: bold;
}
.title {
	padding-top: 12px;
	font-size: 14px !important;
}
.email-invitation-text {
	font-size: 11px;
	letter-spacing: 0px;
	color: var(--primary-font-color);
}
</style>
